import { toCanvasCoords } from '../../utils/canvas';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
}

interface MousePosition {
    x: number;
    y: number;
}

interface MouseDelta {
    dx: number;
    dy: number;
    active: boolean;
}

/**
 * 마우스 드래그 입력 처리
 */
export class MouseInput {
    private renderer: RendererLike | null;
    private mouseActive: boolean;
    private mouseCurrent: MousePosition;
    private delta: MousePosition;
    private canvas: HTMLCanvasElement | null = null;
    private mousedownHandler: ((e: MouseEvent) => void) | null = null;
    private mousemoveHandler: ((e: MouseEvent) => void) | null = null;
    private mouseupHandler: (() => void) | null = null;
    private mouseleaveHandler: (() => void) | null = null;

    constructor() {
        this.renderer = null;
        this.mouseActive = false;
        this.mouseCurrent = { x: 0, y: 0 };
        this.delta = { x: 0, y: 0 };
        this.setup();
    }

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    setup(): void {
        this.canvas = document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!this.canvas) return;

        this.mousedownHandler = (e: MouseEvent) => this.handleDown(e);
        this.mousemoveHandler = (e: MouseEvent) => this.handleMove(e);
        this.mouseupHandler = () => this.handleUp();
        this.mouseleaveHandler = () => this.handleUp();

        this.canvas.addEventListener('mousedown', this.mousedownHandler);
        this.canvas.addEventListener('mousemove', this.mousemoveHandler);
        this.canvas.addEventListener('mouseup', this.mouseupHandler);
        this.canvas.addEventListener('mouseleave', this.mouseleaveHandler);
    }

    destroy(): void {
        if (this.canvas) {
            if (this.mousedownHandler) {
                this.canvas.removeEventListener('mousedown', this.mousedownHandler);
                this.mousedownHandler = null;
            }
            if (this.mousemoveHandler) {
                this.canvas.removeEventListener('mousemove', this.mousemoveHandler);
                this.mousemoveHandler = null;
            }
            if (this.mouseupHandler) {
                this.canvas.removeEventListener('mouseup', this.mouseupHandler);
                this.mouseupHandler = null;
            }
            if (this.mouseleaveHandler) {
                this.canvas.removeEventListener('mouseleave', this.mouseleaveHandler);
                this.mouseleaveHandler = null;
            }
            this.canvas = null;
        }
        this.mouseActive = false;
    }

    handleDown(e: MouseEvent): void {
        const target = e.target as HTMLElement;
        const rect = target.getBoundingClientRect();
        const pos = toCanvasCoords(e.clientX, e.clientY, rect, this.renderer ?? undefined);

        this.mouseActive = true;
        this.mouseCurrent.x = pos.x;
        this.mouseCurrent.y = pos.y;
        this.delta.x = 0;
        this.delta.y = 0;
    }

    handleMove(e: MouseEvent): void {
        if (!this.mouseActive) return;

        const target = e.target as HTMLElement;
        const rect = target.getBoundingClientRect();
        const pos = toCanvasCoords(e.clientX, e.clientY, rect, this.renderer ?? undefined);

        this.delta.x += pos.x - this.mouseCurrent.x;
        this.delta.y += pos.y - this.mouseCurrent.y;
        this.mouseCurrent.x = pos.x;
        this.mouseCurrent.y = pos.y;
    }

    handleUp(): void {
        if (!this.mouseActive) return;

        this.mouseActive = false;
        this.mouseCurrent.x = 0;
        this.mouseCurrent.y = 0;
        this.delta.x = 0;
        this.delta.y = 0;
    }

    resetDelta(): void {
        this.delta.x = 0;
        this.delta.y = 0;
    }

    getDelta(): MouseDelta {
        if (this.mouseActive) {
            return {
                dx: this.delta.x,
                dy: this.delta.y,
                active: true
            };
        }
        return { dx: 0, dy: 0, active: false };
    }

    isActive(): boolean {
        return this.mouseActive;
    }
}
